import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { StatusBadge } from '@medprompt/shared';
import RecordingControls from './RecordingControls';
import FinalizeRecordingPanel from './FinalizeRecordingPanel';
import AudioPlayer from './AudioPlayer';

/**
 * Status, recording controls or playback, prev/next, and the diary toggle.
 * `recording` is the `useConsultationRecording` result and `audio` the `useConsultationAudio` one.
 */
const ConsultationToolbar = ({
  statusEnum,
  recording,
  showAudioPlayer,
  audio,
  navigationContext,
  onNavigatePrev,
  onNavigateNext,
  prevButtonAnimating,
  nextButtonAnimating,
  showPatientDiaryToggle,
  isPatientDiaryVisible,
  onTogglePatientDiary,
  hasPatient,
}) => {
  const {
    isRecording,
    finalizePending,
    finalizeLoading,
    micInterruptReason,
    segmentQueue,
  } = recording;

  return (
    <div className="consultation-toolbar">
      <div className="toolbar-left">
        <StatusBadge
          label={statusEnum.description}
          colorFamily={statusEnum.colorFamily || 'gray'}
          size="normal"
        />
        {isRecording && !finalizePending && !finalizeLoading && (
          <RecordingControls
            elapsedSeconds={recording.elapsedSeconds}
            isPaused={recording.isPaused}
            micLevel={recording.micLevel}
            pauseDisabled={!!micInterruptReason}
            micPickerDisabled={!recording.isPaused || !!micInterruptReason}
            micPickerDisabledReason={
              micInterruptReason
                ? 'Use the microphone options in the warning banner to change device'
                : 'Pause recording to change microphone'
            }
            selectedDeviceId={recording.recoveryDeviceId}
            onSelectDevice={recording.handleSelectRecordingMic}
            onPauseToggle={recording.handlePauseToggle}
            onStop={recording.handleStopRecording}
          />
        )}
        {isRecording && finalizeLoading && !finalizePending && (
          <div className="finalize-recording-panel">
            <div className="finalize-recording-panel-text">
              <strong>Uploading recording</strong>
              <p>Sending the last clip{segmentQueue.length > 1 ? 's' : ''} before processing starts.</p>
            </div>
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
        {isRecording && finalizePending && (
          <FinalizeRecordingPanel
            pendingCount={segmentQueue.length}
            totalSegments={recording.finalizeTotalSegments}
            loading={finalizeLoading}
            error={recording.finalizeError}
            onUploadAndComplete={recording.handleUploadAndComplete}
          />
        )}
        {showAudioPlayer && (
          <AudioPlayer
            ref={audio.audioPlayerRef}
            audioUrl={audio.audioUrl}
            loading={audio.audioLoading}
            error={audio.audioError}
            onTimeUpdate={audio.setAudioCurrentTime}
          />
        )}
      </div>
      <div className="toolbar-right">
        {navigationContext && (
          <>
            <button
              className={`header-button nav-button${prevButtonAnimating ? ' nav-button-animating' : ''}`}
              onClick={onNavigatePrev}
              disabled={navigationContext.currentIndex <= 0}
              title={navigationContext.currentIndex > 0 ? "Previous consultation" : "No previous consultation"}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <span className="nav-position">
              {navigationContext.currentIndex + 1} / {navigationContext.consultationIds.length}
            </span>
            <button
              className={`header-button nav-button${nextButtonAnimating ? ' nav-button-animating' : ''}`}
              onClick={onNavigateNext}
              disabled={navigationContext.currentIndex >= navigationContext.consultationIds.length - 1}
              title={navigationContext.currentIndex < navigationContext.consultationIds.length - 1 ? "Next consultation" : "No next consultation"}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}
        {showPatientDiaryToggle && (
          <button
            className={`patient-diary-toggle ${isPatientDiaryVisible ? 'active' : ''}`}
            onClick={onTogglePatientDiary}
            disabled={!hasPatient}
            title={!hasPatient ? "No patient matched" : "Toggle patient diary"}
          >
            Patient Diary
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConsultationToolbar;
